import { h, setChildren } from "../core/dom.js";
import { api, getToken, setToken } from "../api.js";
import { profile, loadProfile, applyTheme, getBodyMap, setBodyMap, clearUserSession } from "../store.js";
import { navigate } from "../core/router.js";
import { icon } from "../lib/icons.js";
import { topbar } from "../components/common.js";
import { spinner, toast, confirmDialog } from "../components/ui.js";
import { fmtDurationLong, fmtVolume, weightLabel } from "../lib/format.js";
import { notificationsSupported, permissionState, requestPermission, restAlertsEnabled, setRestAlerts } from "../lib/notify.js";
import { pushSupported, enablePush } from "../lib/push.js";
import { disableNativePush } from "../lib/nativePush.js";
import * as health from "../lib/integrations/health.js";
import { restHrEnabled, setRestHrEnabled } from "../lib/restHr.js";
import * as volumeKeys from "../lib/nativeVolumeKeys.js";
import { restoreSettingsDraft, clearSettingsDraft } from "../lib/settingsDrafts.js";
import { editWeeklyPlan, editHomeTools } from "../components/trainingPlan.js";
import { legalLinks } from "./Legal.js";

export default function Profile() {
  const root = h("div", { class: "screen" });
  let tab = new URLSearchParams(location.search).get("tab") === "settings" ? "settings" : "profile";
  let stats = null;
  let version = "";
  const trainingIds=["set-weight_unit","set-rest","set-goal","set-rpe","set-training_goal","set-training_goal_secondary"];
  const bodyIds=["set-bodymap","set-height","set-bw","set-birthyear","set-wake-time"];
  const infoIds=["set-name","set-email"];

  load();
  async function load() {
    setChildren(root, topbar("Profile"), spinner());
    try {
      if (!profile()) await loadProfile();
      stats = await api.stats();
      version = (await fetch("/api/status").then((r) => r.json()).catch(() => ({})))?.version || "";
      render();
      if (tab === "settings" && location.hash === "#weekly-goal") {
        requestAnimationFrame(() => root.querySelector("#set-goal")?.scrollIntoView({ block: "center" }));
      }
    } catch (e) {
      toast(e.message, "error");
      render();
    }
  }

  function render() {
    setChildren(root,
      topbar("Profile"),
      h(
        "div",
        { class: "tabs" },
        h("button", { class: tab === "profile" && "active", onClick: () => { tab = "profile"; render(); } }, "Overview"),
        h("button", { class: tab === "settings" && "active", onClick: () => { tab = "settings"; render(); } }, "Settings")
      ),
      tab === "profile" ? overview() : settings()
    );
    restoreSettingsDraft(root,profile()?.id,[...trainingIds,...bodyIds,...infoIds]);
    root.querySelectorAll('[role="switch"]:not([aria-label])').forEach(sw=>{
      const label=sw.closest(".row-between")?.firstElementChild?.firstElementChild?.textContent;
      if(label)sw.setAttribute("aria-label",label);
    });
  }

  function overview() {
    const p = profile() || {};
    return h(
      "div",
      null,
      h(
        "div",
        { class: "card hstack" },
        p.gravatar_url
          ? h("img", { src: p.gravatar_url, style: { width: "56px", height: "56px", borderRadius: "50%" } })
          : h("div", { class: "auth-logo", style: { margin: 0 } }, h("div", { class: "mark", style: { width: "56px", height: "56px" } }, icon("user", { size: 28 }))),
        h("div", null, h("div", { style: { fontWeight: 700, fontSize: "1.15rem" } }, p.display_name || p.username), h("div", { class: "dim" }, `@${p.username}`))
      ),
      stats
        ? h(
            "div",
            { class: "stat-grid mt" },
            stat(String(stats.total_sessions), "Workouts"),
            stat(String(stats.total_sets), "Sets logged"),
            stat(fmtDurationLong(stats.total_duration_seconds), "Time training"),
            stat(fmtVolume(stats.total_volume_kg).split(" ")[0], `Total volume (${weightLabel()})`)
          )
        : null,
      h("div", { class: "section-title" }, "Track"),
      h("div", { class: "profile-shortcuts" },
        profileShortcut("weight", "Bodyweight", "/weight"),
        profileShortcut("ruler", "Measurements", "/measurements"),
        profileShortcut("camera", "Photos", "/progress/photos"),
        profileShortcut("trophy", "Achievements", "/achievements")
      ),
      h("div", { class: "section-title" }, "Community"),
      h("div", { class: "profile-shortcuts" },
        profileShortcut("users", "Friends", "/friends"),
        profileShortcut("search", "Discover", "/discover")
      ),
      p.is_admin ? h("button", { class: "btn btn-ghost btn-block mt", onClick: () => navigate("/admin") }, icon("shield", { size: 18 }), "Admin dashboard") : null,
      h("button", { class: "btn btn-ghost btn-block mt", onClick: logout }, icon("logout", { size: 18 }), "Log out")
    );
  }

  function profileShortcut(iconName, label, path) {
    return h("button", { class: "profile-shortcut", onClick: () => navigate(path) },
      icon(iconName, { size: 18 }),
      h("span", null, label)
    );
  }

  function settings() {
    return h(
      "div",
      null,
      h("div", { class: "section-title", style: { marginTop: "4px" } }, "Training & Gym"),
      h("p",{class:"dim draft-status",role:"status"}),
      trainingCard(),

      h("div", { class: "section-title" }, "Body & Health"),
      bodyCard(),

      h("div", { class: "section-title" }, "Notifications & Reminders"),
      h("p",{class:"dim"},"Notification changes save immediately."),
      notificationsCard(),

      // Native-only (Kashtu Android app): Mi Band / Health Connect sync. Hidden on plain web/PWA.
      health.available() ? h("div", { class: "section-title" }, "Health Connect") : null,
      health.available() ? healthCard() : null,

      // Native-only: hardware controls the PWA has no way to offer.
      volumeKeys.available() ? h("div", { class: "section-title" }, "Workout controls") : null,
      volumeKeys.available() ? volumeKeyCard() : null,

      h("div", { class: "section-title" }, "Account & Social"),
      socialCard(),

      h("div", { class: "section-title" }, "Security"),
      securityCard(),

      h("div", { class: "section-title" }, "Data & Account"),
      dataAccountCard(),

      h("div", { class: "section-title" }, "Appearance & About"),
      h("p",{class:"dim"},"Appearance changes save immediately."),
      appearanceAboutCard(),

      h("div", { style: { marginTop: "24px" } }, legalLinks()),
      h("p", { class: "center", style: { marginTop: "8px", fontSize: "0.75rem" } },
        h("button", { class: "btn-ghost-link", onClick: () => navigate("/changelog") },
          version ? `Kashtu v${version}` : "Kashtu", " · What's new"))
    );
  }

  function trainingCard() {
    const p = profile() || {};
    return h(
      "div",
      { class: "card" },
      field("Weight unit",selectEl("weight_unit",p.weight_unit,[["kg","Kilograms (kg)"],["lbs","Pounds (lbs)"]])),
      field("Default rest (seconds)",h("input",{class:"input",id:"set-rest",type:"number",min:0,max:3600,value:p.default_rest_seconds ?? 90})),
      field("Weekly goal (workouts per week)",h("input",{class:"input",id:"set-goal",type:"number",min:1,max:14,step:1,value:p.weekly_goal ?? 3})),
      h("p",{class:"dim"},"The weekly goal sets your default and this week's target. Earlier goals remain unchanged."),
      h("button",{class:"btn btn-ghost btn-block",onClick:()=>editWeeklyPlan()},"Plan this week, next week, or a break"),
      h("label",{class:"check-row"},h("input",{id:"set-rpe",type:"checkbox",checked:!p.hide_rpe}),"Show RPE column in logger"),
      trainingGoalSection(),
      h("button", { class: "btn btn-primary btn-block mt", onClick: saveTrainingPrefs }, "Save training preferences")
    );
  }

  function bodyCard() {
    const p = profile() || {};
    return h(
      "div",
      { class: "card" },
      field("Sex",selectEl("bodymap",p.gender || getBodyMap(),[["male","Male"],["female","Female"]])),
      field("Height (cm)", h("input", { class: "input", id: "set-height", type: "number", step: "0.1", value: p.height_cm ?? "" })),
      field("Bodyweight (kg)", h("input", { class: "input", id: "set-bw", type: "number", step: "0.05", value: p.bodyweight_kg ?? "" })),
      field("Birth year", h("input", { class: "input", id: "set-birthyear", type: "number", inputmode: "numeric", step: "1", min: "1900", placeholder: "e.g. 1990", value: p.birth_year ?? "" })),
      h("div", { class: "field" },
        h("label", { for: "set-wake-time" }, "Usual wake-up time (optional)"),
        h("input", { class: "input", id: "set-wake-time", type: "time", value: p.wake_time || "" }),
        h("div", { class: "dim", style: { fontSize: ".78rem", marginTop: "4px" } },
          "Weigh-ins within 3 hours before or after this time are marked first-morning, post-void by default.")
      ),
      h("button", { class: "btn btn-primary btn-block mt", onClick: saveBodyPrefs }, "Save body details")
    );
  }

  function securityCard() {
    return h(
      "form",
      { class: "card", onSubmit: changePassword },
      field("Current password", h("input", { class: "input", name: "current", type: "password", autocomplete: "current-password" })),
      field("New password", h("input", { class: "input", name: "next", type: "password", autocomplete: "new-password", minlength: 6 })),
      h("button", { class: "btn btn-ghost btn-block", type: "submit" }, "Change password")
    );
  }

  function dataAccountCard() {
    return h(
      "div",
      { class: "card" },
      h("button", { class: "btn btn-ghost btn-block", onClick: exportData }, icon("download", { size: 18 }), "Export my data"),
      h("button", { class: "btn btn-block mt", style: { background: "transparent", color: "var(--danger)" }, onClick: deleteAccount }, icon("trash", { size: 18 }), "Delete account")
    );
  }

  function appearanceAboutCard() {
    const p = profile() || {};
    return h(
      "div",
      { class: "card" },
      field("Theme", selectEl("theme", p.theme || "system", [["system", "System"], ["light", "Light"], ["dark", "Dark"]], async (v) => {
        applyTheme(v);
        try { profile(await api.updateMe({ theme: v })); toast("Theme updated", "success"); }
        catch (e) { toast(e.message, "error"); }
      })),
      h("div", { class: "btn-row mt" },
        h("button", { class: "btn btn-ghost grow", onClick: () => navigate("/welcome") }, icon("info", { size: 18 }), "Setup tour"),
        h("button", { class: "btn btn-ghost grow", onClick: () => navigate("/changelog") }, icon("list", { size: 18 }), "What's new")
      )
    );
  }

  function field(label, control) {
    control.id ||= `settings-${label.toLowerCase().replace(/[^a-z0-9]+/g,"-")}`;
    return h("div", { class: "field" }, h("label", { for: control.id }, label), control);
  }

  function socialCard() {
    const p = profile() || {};
    const on = p.share_activity == null ? true : !!p.share_activity;
    const priv = p.is_private == null ? true : !!p.is_private;
    const notify = !!p.notify_friends;
    return h("div", { class: "card" },
      field("Display name", h("input", { class: "input", id: "set-name", value: p.display_name || "" })),
      field("Email", h("input", { class: "input", id: "set-email", type: "email", value: p.email || "", placeholder: "you@example.com" })),
      emailStatus(p),
      h("button", { class: "btn btn-primary btn-block mt", onClick: saveInfo }, "Save profile info"),

      h("p",{class:"dim mt"},"Privacy and sharing changes below save immediately."),
      h("div", { class: "row-between", style: { borderTop: "1px solid var(--border)", marginTop: "16px", paddingTop: "14px" } },
        h("div", null,
          h("div", { style: { fontWeight: 600 } }, "Private account"),
          h("div", { class: "dim", style: { fontSize: ".82rem" } }, "Approve who can follow you. Turn off to let anyone follow without approval.")
        ),
        toggle(priv, async (want) => {
          try { profile(await api.updateMe({ is_private: want })); toast(want ? "Account is private" : "Account is public", "success"); }
          catch (e) { toast(e.message, "error"); }
          render();
        })
      ),
      h("div", { class: "row-between", style: { borderTop: "1px solid var(--border)", marginTop: "14px", paddingTop: "14px" } },
        h("div", null,
          h("div", { style: { fontWeight: 600 } }, "Share activity with followers"),
          h("div", { class: "dim", style: { fontSize: ".82rem" } }, "Let followers see your workouts, training status, and stats. Turn off to keep them private.")
        ),
        toggle(on, async (want) => {
          try { profile(await api.updateMe({ share_activity: want })); toast(want ? "Sharing on" : "Sharing off", "success"); }
          catch (e) { toast(e.message, "error"); }
          render();
        })
      ),
      pushSupported()
        ? h("div", { class: "row-between", style: { borderTop: "1px solid var(--border)", marginTop: "14px", paddingTop: "14px" } },
            h("div", null,
              h("div", { style: { fontWeight: 600 } }, "Follow notifications"),
              h("div", { class: "dim", style: { fontSize: ".82rem" } }, "Get notified about new followers/requests and when people you follow start a workout.")
            ),
            toggle(notify, async (want) => {
              try {
                if (want) await enablePush();
                profile(await api.updateMe({ notify_friends: want }));
                toast(want ? "Friend notifications on" : "Friend notifications off", "success");
              } catch (e) {
                toast(e.message || "Couldn't enable notifications", "error");
              }
              render();
            })
          )
        : null
    );
  }

  function notificationsCard() {
    if (!notificationsSupported()) {
      return h("div", { class: "card" }, h("p", { class: "dim" }, "Notifications aren't supported in this browser. On iPhone, add the app to your Home Screen first."));
    }
    const state = permissionState();
    const granted = state === "granted";
    return h(
      "div",
      { class: "card" },
      h("div", { class: "row-between" },
        h("div", null,
          h("div", { style: { fontWeight: 600 } }, "Rest timer alerts"),
          h("div", { class: "dim", style: { fontSize: ".82rem" } }, "Buzz when your rest is over, even if the screen is off.")
        ),
        toggle(restAlertsEnabled() && granted, async (on) => {
          if (on && !granted) {
            const res = await requestPermission();
            if (res !== "granted") { toast("Notifications were not allowed", "warn"); render(); return; }
          }
          setRestAlerts(on);
          render();
        })
      ),
      state === "denied" ? h("p", { class: "dim", style: { marginTop: "10px" } }, "Notifications are blocked in your browser settings for this site — enable them there to use alerts.") : null,
      state === "default" ? h("button", { class: "btn btn-ghost btn-block mt", onClick: async () => { await requestPermission(); render(); } }, icon("info", { size: 16 }), "Enable notifications") : null,

      // ── Workout reminders (Web Push) ──
      pushSupported() ? remindersBlock() : null,
      // ── Weight reminders (Web Push) ──
      pushSupported() ? weightRemindersBlock() : null,
      // ── Weekly training recap (Web Push) ──
      pushSupported() ? weeklyRecapBlock() : null
    );
  }

  // Mi Band / Health Connect (native Android shell only). Permission status + manual sync; the
  // background 15-min catch-up runs natively (WorkManager) once connected. Self-refreshing card.
  function healthCard() {
    const card = h("div", { class: "card" }, h("p", { class: "dim" }, "Checking Health Connect…"));
    const refresh = async () => {
      const st = await health.getStatus().catch(() => ({ available: true, granted: false }));
      const granted = !!st.granted;
      setChildren(card,
        h("div", { class: "row-between" },
          h("div", null,
            h("div", { style: { fontWeight: 600 } }, "Mi Band sync"),
            h("div", { class: "dim", style: { fontSize: ".82rem" } },
              granted ? "Connected — workouts & heart rate import automatically." : "Import Band 9 Pro workouts and heart rate from Health Connect.")
          ),
          h("span", { class: "chip", style: { color: granted ? "var(--success)" : "var(--dim)" } }, granted ? "Connected" : "Off")
        ),
        granted
          ? h("button", { class: "btn btn-ghost btn-block mt", onClick: () => doSync() }, icon("history", { size: 16 }), "Sync now")
          : h("button", { class: "btn btn-primary btn-block mt", onClick: () => doConnect() }, icon("pulse", { size: 16 }), "Connect Health Connect"),
        st.error ? h("p", { class: "dim", style: { marginTop: "8px", fontSize: ".8rem" } }, "Health Connect isn't available on this device.") : null,
        granted ? restHrBlock() : null
      );
    };
    const doConnect = async () => {
      try { await health.connect(); toast("Health Connect linked", "success"); await health.syncNow().catch(() => {}); }
      catch (e) { toast(e.message, "error"); }
      refresh();
    };
    const doSync = async () => {
      try { const r = await health.syncNow(); toast(r.synced ? `Synced ${r.synced} workout(s)` : "Up to date", "success"); }
      catch (e) { toast(e.message, "error"); }
    };
    refresh();
    return card;
  }

  // Opt-in, per device, and off by default. During a lifting workout the live feed means a
  // foreground service and an ongoing notification for the whole session — worth it if you want
  // the recovery hint, not worth switching on for everyone. Cardio sessions already run the feed
  // for the BPM readout, so there it costs nothing extra either way.
  function restHrBlock() {
    // Its own switch rather than toggle(): that helper captures its state at build time and
    // relies on the profile signal re-rendering the card, and this preference is per-device
    // localStorage with nothing to re-render it. So flip the node in place — which is also the
    // rule the rest of this codebase follows, since a rebuilt control loses the tap that
    // triggered it on touch.
    const sw = h("button", {
      class: ["switch", restHrEnabled() && "on"], role: "switch",
      "aria-checked": restHrEnabled() ? "true" : "false",
      "aria-label": "Rest recovery hint",
      onClick: () => {
        const want = !restHrEnabled();
        setRestHrEnabled(want);
        sw.classList.toggle("on", want);
        sw.setAttribute("aria-checked", want ? "true" : "false");
        toast(want ? "Heart rate will show during your rests" : "Rest recovery hint off", "success");
      },
    }, h("span", { class: "switch-knob" }));

    return h(
      "div",
      { style: { borderTop: "1px solid var(--border)", marginTop: "14px", paddingTop: "14px" } },
      h("div", { class: "row-between" },
        h("div", null,
          h("div", { style: { fontWeight: 600 } }, "Rest recovery hint"),
          h("div", { class: "dim", style: { fontSize: ".82rem" } },
            "Show your heart rate during rests and note when it's back down.")
        ),
        sw
      ),
      // Say the limitation on the switch, not in a support thread later: the band's write cadence
      // is why this is a hint beside the countdown and never a condition on it.
      h("p", { class: "dim", style: { fontSize: ".78rem", marginTop: "8px", marginBottom: 0, lineHeight: 1.45 } },
        "Your band reports about once a minute, so this is a suggestion alongside the timer — it never holds a rest open or ends one early. Keeping the feed running during a lifting workout uses a little more battery.")
    );
  }

  // Hardware volume-down completes the current set. Off by default and per device, because it
  // takes over a system control — see lib/nativeVolumeKeys.js for how it fails open.
  function volumeKeyCard() {
    const sw = h("button", {
      class: ["switch", volumeKeys.volumeKeyEnabled() && "on"], role: "switch",
      "aria-checked": volumeKeys.volumeKeyEnabled() ? "true" : "false",
      "aria-label": "Volume button completes a set",
      onClick: () => {
        const want = !volumeKeys.volumeKeyEnabled();
        volumeKeys.setVolumeKeyEnabled(want);
        sw.classList.toggle("on", want);
        sw.setAttribute("aria-checked", want ? "true" : "false");
        toast(want ? "Volume down will complete your set" : "Volume button back to normal", "success");
      },
    }, h("span", { class: "switch-knob" }));

    return h("div", { class: "card" },
      h("div", { class: "row-between" },
        h("div", null,
          h("div", { style: { fontWeight: 600 } }, "Volume button completes a set"),
          h("div", { class: "dim", style: { fontSize: ".82rem" } },
            "Press volume down to tick off the current set and start your rest.")
        ),
        sw
      ),
      h("p", { class: "dim", style: { fontSize: ".78rem", marginTop: "8px", marginBottom: 0, lineHeight: 1.45 } },
        "Only while a workout is open, and only volume down — volume up still changes the volume, and the button returns to normal the moment you leave the workout screen. Takes effect on your next workout.")
    );
  }

  function weightRemindersBlock() {
    const p = profile() || {};
    const on = !!p.weight_reminder_enabled;
    const hour = p.weight_reminder_hour ?? 8;
    return h(
      "div",
      { style: { borderTop: "1px solid var(--border)", marginTop: "14px", paddingTop: "14px" } },
      h("div", { class: "row-between" },
        h("div", null,
          h("div", { style: { fontWeight: 600 } }, "Weight reminders"),
          h("div", { class: "dim", style: { fontSize: ".82rem" } }, "A daily reminder to log your bodyweight, skipped once you've weighed in.")
        ),
        toggle(on, async (want) => {
          try {
            if (want) await enablePush();
            profile(await api.updateMe({ weight_reminder_enabled: want, weight_reminder_hour: hour }));
            toast(want ? "Weight reminders on" : "Weight reminders off", "success");
          } catch (e) {
            toast(e.message || "Couldn't enable reminders", "error");
          }
          render();
        })
      ),
      on
        ? h("div", { class: "field", style: { marginTop: "12px" } },
            h("label", null, "Remind me around"),
            h("select", { class: "select", onchange: async (e) => { profile(await api.updateMe({ weight_reminder_hour: parseInt(e.target.value) })); } },
              ...Array.from({ length: 24 }, (_, i) => i).map((hr) => h("option", { value: hr, selected: hr === hour }, `${String(hr).padStart(2, "0")}:00`))
            )
          )
        : null
    );
  }

  function weeklyRecapBlock() {
    const p = profile() || {};
    const on = p.recap_enabled == null ? true : !!p.recap_enabled;
    return h(
      "div",
      { style: { borderTop: "1px solid var(--border)", marginTop: "14px", paddingTop: "14px" } },
      h("div", { class: "row-between" },
        h("div", null,
          h("div", { style: { fontWeight: 600 } }, "Weekly training recap"),
          h("div", { class: "dim", style: { fontSize: ".82rem" } }, "A Sunday evening push summary of workouts completed and volume lifted.")
        ),
        toggle(on, async (want) => {
          try {
            if (want) await enablePush();
            profile(await api.updateMe({ recap_enabled: want }));
            toast(want ? "Weekly recap on" : "Weekly recap off", "success");
          } catch (e) {
            toast(e.message || "Couldn't update recap setting", "error");
          }
          render();
        })
      )
    );
  }

  function remindersBlock() {
    const p = profile() || {};
    const on = !!p.reminders_enabled;
    const hour = p.reminder_hour ?? 18;
    return h(
      "div",
      { style: { borderTop: "1px solid var(--border)", marginTop: "14px", paddingTop: "14px" } },
      h("div", { class: "row-between" },
        h("div", null,
          h("div", { style: { fontWeight: 600 } }, "Workout reminders"),
          h("div", { class: "dim", style: { fontSize: ".82rem" } }, "A daily nudge if you haven't trained and are short of your weekly goal.")
        ),
        toggle(on, async (want) => {
          try {
            if (want) await enablePush();
            profile(await api.updateMe({ reminders_enabled: want, reminder_hour: hour }));
            toast(want ? "Reminders on" : "Reminders off", "success");
          } catch (e) {
            toast(e.message || "Couldn't enable reminders", "error");
          }
          render();
        })
      ),
      on
        ? h("div", { class: "field", style: { marginTop: "12px" } },
            h("label", null, "Remind me around"),
            h("select", { class: "select", onchange: async (e) => { profile(await api.updateMe({ reminder_hour: parseInt(e.target.value) })); } },
              ...Array.from({ length: 17 }, (_, i) => i + 6).map((hr) => h("option", { value: hr, selected: hr === hour }, `${hr}:00`))
            )
          )
        : null,
      h("button", { class: "btn btn-ghost btn-block mt", onClick: sendTestPush }, icon("info", { size: 16 }), "Send a test notification")
    );
  }

  async function sendTestPush() {
    try {
      await enablePush();
      const res = await api.pushTest();
      toast(res.sent > 0 ? "Test sent — check your notifications" : "Subscribed, but no device received it", res.sent > 0 ? "success" : "warn");
    } catch (e) {
      toast(e.message || "Test failed", "error");
    }
  }

  function toggle(on, onChange) {
    const el = h("button", { class: ["switch", on && "on"], role: "switch", "aria-checked": on ? "true" : "false", onClick: () => onChange(!on) }, h("span", { class: "switch-knob" }));
    return el;
  }

  const GOAL_CHOICES = [["strength","Strength"],["hypertrophy","Muscle growth"],["endurance","Endurance"],["general","General fitness"]];
  function trainingGoalSection() {
    const p=profile() || {};
    return h("div",{class:"mt"},
      field("Main training goal",selectEl("training_goal",p.training_goal || "",[["","Not set"],...GOAL_CHOICES])),
      field("Secondary goal (optional)",selectEl("training_goal_secondary",p.training_goal_secondary || "",[["","None"],...GOAL_CHOICES])),
      h("p",{class:"dim"},"Goals guide rep ranges, coaching, and your default review. A duplicate secondary goal is cleared on save."),
      h("button",{class:"btn btn-ghost btn-block",onClick:()=>editHomeTools()},"Customize Home shortcuts"));
  }

  function selectEl(id, value, options, onChange) {
    return h(
      "select",
      { class: "select", id: `set-${id}`, onchange: onChange ? (e) => onChange(e.target.value) : null },
      ...options.map(([v, l]) => h("option", { value: v, selected: v === value }, l))
    );
  }

  async function saveTrainingPrefs() {
    const data = {
      default_rest_seconds: Number(root.querySelector("#set-rest").value),
      weight_unit: root.querySelector("#set-weight_unit").value,
      weekly_goal: Number(root.querySelector("#set-goal").value),
      hide_rpe: !root.querySelector("#set-rpe").checked,
      training_goal: root.querySelector("#set-training_goal").value || null,
      training_goal_secondary: root.querySelector("#set-training_goal_secondary").value || null,
    };
    try {
      profile(await api.updateMe(data));
      clearSettingsDraft(profile().id,trainingIds);render();
      toast("Training preferences saved", "success");
    } catch (e) {
      toast(e.message, "error");
    }
  }

  async function saveBodyPrefs() {
    const data = {
      gender: root.querySelector("#set-bodymap").value,
      wake_time: root.querySelector("#set-wake-time")?.value || null,
      bodyweight_kg: root.querySelector("#set-bw")?.value === "" ? null : parseFloat(root.querySelector("#set-bw")?.value),
      height_cm: root.querySelector("#set-height")?.value === "" ? null : parseFloat(root.querySelector("#set-height")?.value),
      birth_year: root.querySelector("#set-birthyear")?.value === "" ? null : parseInt(root.querySelector("#set-birthyear")?.value, 10),
    };
    try {
      profile(await api.updateMe(data));
      setBodyMap(data.gender);clearSettingsDraft(profile().id,bodyIds);render();
      toast("Body details saved", "success");
    } catch (e) {
      toast(e.message, "error");
    }
  }

  async function savePrefs() {
    await saveTrainingPrefs();
    await saveBodyPrefs();
  }

  async function saveInfo() {
    const data = { display_name: root.querySelector("#set-name").value, email: root.querySelector("#set-email").value || null };
    try {
      profile(await api.updateMe(data));
      clearSettingsDraft(profile().id,infoIds);
      toast("Profile saved", "success");
      render();
    } catch (e) {
      toast(e.message, "error");
    }
  }

  // Verification status under the email field. Confirming your email unlocks public
  // actions (publishing, following, ratings); show a resend button while unverified.
  function emailStatus(p) {
    if (!p.email) return null;
    if (p.email_verified)
      return h("p", { class: "dim", style: { fontSize: ".8rem", marginTop: "-4px" } }, icon("check", { size: 14 }), " Email verified");
    return h(
      "div",
      { style: { marginTop: "-4px" } },
      h("p", { style: { fontSize: ".8rem", color: "var(--warn)" } }, "Email not confirmed — confirm it to publish, follow people, and rate workouts."),
      h("button", { class: "btn btn-ghost", type: "button", style: { marginTop: "6px", padding: "8px 14px", fontSize: ".85rem" }, onClick: resendVerification }, "Resend confirmation email")
    );
  }

  async function resendVerification() {
    try {
      const r = await api.resendVerification();
      toast(r.already_verified ? "Already verified." : "Confirmation email sent — check your inbox.", "success");
    } catch (e) {
      toast(e.message, "error");
    }
  }

  async function changePassword(e) {
    e.preventDefault();
    const f = new FormData(e.target);
    try {
      await api.changePassword({ current_password: f.get("current"), new_password: f.get("next") });
      e.target.reset();
      toast("Password changed", "success");
    } catch (err) {
      toast(err.message, "error");
    }
  }

  async function exportData() {
    try {
      const res = await fetch(api.exportUrl, { headers: { Authorization: `Bearer ${getToken()}` } });
      const blob = await res.blob();
      const url = URL.createObjectURL(blob);
      const a = h("a", { href: url, download: `workout-tracker-export-${new Date().toISOString().slice(0, 10)}.json` });
      document.body.appendChild(a);
      a.click();
      a.remove();
      URL.revokeObjectURL(url);
      toast("Export downloaded", "success");
    } catch (e) {
      toast("Export failed", "error");
    }
  }

  async function deleteAccount() {
    const ok = await confirmDialog({ title: "Delete account?", message: "This permanently deletes your account and all workouts, sessions, and records. This cannot be undone.", confirmText: "Delete everything", danger: true });
    if (!ok) return;
    try {
      await api.deleteAccount();
      try { await disableNativePush(); } catch {}
      try { await health.disconnect(); } catch {}
      clearUserSession();
      navigate("/login", { replace: true });
      location.reload();
    } catch (e) {
      toast(e.message, "error");
    }
  }

  async function logout() {
    try { await disableNativePush(); } catch {}
    try { await health.disconnect(); } catch {}
    clearUserSession();
    location.href = "/login";
  }

  function stat(num, label) {
    return h("div", { class: "stat" }, h("div", { class: "num accent" }, num), h("div", { class: "lbl" }, label));
  }

  return root;
}
